import React, { useEffect, useState, useCallback } from 'react';
import { merchantApi } from '../../api/config';
import { fmtMoney } from '../../utils/formatMoney';
import bankLogos from 'thai-banks-logo/banks-logo.json';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faMoneyBillWave, faSearch, faUpload, faXmark, faExclamationTriangle,
  faCheckCircle, faHourglass, faTimesCircle, faBan, faPaperclip, faRotate,
} from '@fortawesome/free-solid-svg-icons';
import DataTable, { Column } from '../../components/shared/DataTable';
import MerchantMultiSelect from '../../components/shared/MerchantMultiSelect';
import { UiPageHeader, UiModal, StatusBadge } from '../../components/ui';
import type { StatusDef } from '../../components/ui';
import dayjs from 'dayjs';

interface PayoutTopup {
  id: string;
  merchant_id: string;
  bank_id?: string;
  bank_name?: string;       // ธนาคารผู้โอน
  bank_number?: string;     // เลขบัญชีผู้โอน
  amount: number;
  fee_amount?: number;
  net_amount?: number;
  slip_url?: string;
  status: 'pending' | 'paid' | 'expired' | 'cancelled';
  amount_mismatch?: boolean;
  requested_amount?: number; // ยอดเดิมก่อนปรับ (กรณีสลิปเกิน/ขาด — amount = ยอดสลิปจริง)
  approval_method?: string;
  approved_by?: string;
  created_at: string;
  paid_at?: string;
  merchant?: { id: string; name: string; email: string } | null;
  receiving_bank?: { name: string; account_number: string; bank_code: string } | null;
}

const TOPUP_STATUS_MAP: Record<string, StatusDef> = {
  pending: { bg: 'bg-amber-100 dark:bg-amber-900/30', text: 'text-amber-700 dark:text-amber-400', icon: faHourglass, label: 'รอแนบสลิป' },
  paid: { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-700 dark:text-green-400', icon: faCheckCircle, label: 'สำเร็จ' },
  expired: { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-600 dark:text-red-400', icon: faTimesCircle, label: 'หมดอายุ' },
  cancelled: { bg: 'bg-gray-100 dark:bg-slate-700', text: 'text-gray-600 dark:text-slate-300', icon: faBan, label: 'ยกเลิก' },
};

const bankLogo = (code?: string) => {
  const key = String(code || '').toUpperCase().replace('CIMBT', 'CIMB').replace('UOBT', 'UOB').replace('LH BANK', 'LHB') as keyof typeof bankLogos;
  return bankLogos[key]?.icon || null;
};
const fmtSlip = (u?: string) => (!u ? '' : (!u.startsWith('http') && !u.startsWith('data:') ? `data:image/png;base64,${u}` : u));

const PayoutTopupsPage: React.FC = () => {
  const [rows, setRows] = useState<PayoutTopup[]>([]);
  const [loading, setLoading] = useState(true);
  const today = dayjs().format('YYYY-MM-DD');
  const [status, setStatus] = useState('');
  const [dateFrom, setDateFrom] = useState(today);
  const [dateTo, setDateTo] = useState(today);
  const [search, setSearch] = useState('');
  const [merchants, setMerchants] = useState<{ id: string; name: string; email: string }[]>([]);
  const [merchantId, setMerchantId] = useState('');

  // สิทธิ์ "บังคับแนบ" (ข้ามการตรวจสอบ) — super_admin หรือมี approve_slip_no_verify
  const adminUser = JSON.parse(localStorage.getItem('admin_user') || '{}');
  const isSuperAdmin = (adminUser.type === 'admin_employee' && adminUser.role === 'super_admin') || !adminUser.type;
  const canForce = isSuperAdmin || adminUser.permissions?.approve_slip_no_verify === true;

  // modal
  const [selected, setSelected] = useState<PayoutTopup | null>(null);
  const [slip, setSlip] = useState('');
  const [processing, setProcessing] = useState<'verify' | 'force' | null>(null);
  const [err, setErr] = useState('');
  // slip preview
  const [viewSlip, setViewSlip] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = { limit: 100, offset: 0 };
      if (status) params.status = status;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (search.trim()) params.search = search.trim();
      if (merchantId) params.merchant_id = merchantId;
      const res = await merchantApi.get('/admin/payout-topups', { params });
      if (res.data.success) setRows(res.data.data || []);
    } catch {
      toast.error('โหลดข้อมูลไม่สำเร็จ');
    } finally {
      setLoading(false);
    }
  }, [status, dateFrom, dateTo, search, merchantId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    merchantApi.get('/merchants').then(res => setMerchants(res.data.data || [])).catch(() => {});
  }, []);

  const openAttach = (t: PayoutTopup) => { setSelected(t); setSlip(''); setErr(''); };
  const closeAttach = () => { setSelected(null); setSlip(''); setErr(''); setProcessing(null); };

  const onSlipChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => setSlip(reader.result as string);
    reader.readAsDataURL(file);
  };

  const complete = async (force: boolean) => {
    if (!selected) return;
    if (!slip) { setErr('กรุณาแนบรูปสลิป'); return; }
    setErr(''); setProcessing(force ? 'force' : 'verify');
    try {
      const res = await merchantApi.post(`/admin/payout-topups/${selected.id}/complete`, { slip_base64: slip, force });
      if (res.data.success) {
        toast.success(res.data.message || 'สำเร็จ');
        closeAttach();
        load();
      } else {
        setErr(res.data.message || 'ดำเนินการไม่สำเร็จ');
      }
    } catch (e: any) {
      setErr(e.response?.data?.message || 'ดำเนินการไม่สำเร็จ');
    } finally {
      setProcessing(null);
    }
  };

  const columns: Column<PayoutTopup>[] = [
    {
      key: 'created_at', header: 'วันที่', render: (t) => (
        <div className="text-xs">
          <div className="text-gray-700 dark:text-slate-200">{dayjs(t.created_at).format('DD/MM/YYYY')}</div>
          <div className="text-gray-400 dark:text-slate-500">{dayjs(t.created_at).format('HH:mm')}</div>
        </div>
      )
    },
    {
      key: 'merchant', header: 'Merchant', render: (t) => (
        <div>
          <div className="font-medium text-gray-700 dark:text-slate-200 text-sm">{t.merchant?.name || '-'}</div>
          <div className="text-xs text-gray-400 dark:text-slate-500">{t.merchant?.email || ''}</div>
        </div>
      )
    },
    {
      key: 'sender', header: 'ผู้โอน', render: (t) => (
        <div className="flex items-center gap-1.5">
          {bankLogo(t.bank_name) && <img src={bankLogo(t.bank_name)!} alt={t.bank_name} className="w-5 h-5 rounded-full object-contain bg-white" />}
          <div className="text-xs">
            <div className="font-medium text-gray-700 dark:text-slate-200">{t.bank_name || '-'}</div>
            <div className="text-gray-400 dark:text-slate-500 font-mono">{t.bank_number || '-'}</div>
          </div>
        </div>
      )
    },
    {
      key: 'amount', header: 'ยอด', render: (t) => (
        <div className="text-sm">
          <div className="font-bold" style={{ color: 'var(--theme-accent)' }}>฿{fmtMoney(t.amount)}</div>
          {t.fee_amount ? <div className="text-[11px] text-amber-600 dark:text-amber-400">fee -฿{fmtMoney(t.fee_amount)}</div> : null}
          {t.net_amount ? <div className="text-[11px] text-emerald-600 dark:text-emerald-400">net ฿{fmtMoney(t.net_amount)}</div> : null}
          {t.amount_mismatch && <div className="text-[10px] text-orange-600">⚠ {t.requested_amount != null ? (t.amount >= t.requested_amount ? 'ยอดเกิน' : 'ยอดขาด') : 'ยอดไม่ตรง'}</div>}
        </div>
      )
    },
    {
      key: 'status', header: 'สถานะ', render: (t) => (
        <div>
          <StatusBadge status={t.status} statusMap={TOPUP_STATUS_MAP} />
          {t.approval_method === 'manual_admin' && <div className="text-[10px] text-gray-400 mt-0.5">บังคับโดย {t.approved_by}</div>}
        </div>
      )
    },
    {
      key: 'slip', header: 'สลิป', render: (t) => (
        t.slip_url
          ? <img
              src={fmtSlip(t.slip_url)}
              alt="slip"
              loading="lazy"
              onClick={() => setViewSlip(fmtSlip(t.slip_url))}
              className="w-12 h-12 rounded-lg object-cover border border-gray-200 dark:border-slate-600 cursor-zoom-in hover:opacity-80 transition-opacity"
            />
          : <span className="text-gray-300 dark:text-slate-600">-</span>
      )
    },
    {
      key: 'actions', header: '', render: (t) => (
        t.status === 'pending'
          ? <button onClick={() => openAttach(t)} className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white flex items-center gap-1.5" style={{ background: 'var(--theme-primary)' }}><FontAwesomeIcon icon={faUpload} /> แนบสลิป</button>
          : null
      )
    },
  ];

  return (
    <div className="space-y-4">
      <UiPageHeader icon={faMoneyBillWave} title="เติมเงิน Payout (ลูกค้า)" subtitle="รายการเติมเงินเข้า Payout ของลูกค้า — ช่วยแนบสลิป/บังคับแนบแทนได้" />

      {/* Filters */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-theme p-4 flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-[11px] text-gray-500 dark:text-slate-400 mb-1">สถานะ</label>
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="rounded-xl border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-700 px-3 py-2 text-sm">
            <option value="">ทั้งหมด</option>
            <option value="pending">รอแนบสลิป</option>
            <option value="paid">สำเร็จ</option>
            <option value="expired">หมดอายุ</option>
            <option value="cancelled">ยกเลิก</option>
          </select>
        </div>
        <div className="min-w-[180px] flex-1 sm:flex-none">
          <label className="block text-[11px] text-gray-500 dark:text-slate-400 mb-1">Merchant</label>
          <MerchantMultiSelect
            merchants={merchants}
            value={merchantId}
            onChange={(v) => setMerchantId(v)}
            variant="light"
          />
        </div>
        <div className="flex gap-2 w-full sm:w-auto">
          <div className="flex-1 sm:flex-none">
            <label className="block text-[11px] text-gray-500 dark:text-slate-400 mb-1">ตั้งแต่</label>
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-full rounded-xl border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-700 px-3 py-2 text-sm" />
          </div>
          <div className="flex-1 sm:flex-none">
            <label className="block text-[11px] text-gray-500 dark:text-slate-400 mb-1">ถึง</label>
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-full rounded-xl border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-700 px-3 py-2 text-sm" />
          </div>
        </div>
        <div className="flex-1 min-w-[160px]">
          <label className="block text-[11px] text-gray-500 dark:text-slate-400 mb-1">ค้นหา (เลขบัญชีผู้โอน)</label>
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && load()} placeholder="เลขบัญชีผู้โอน" className="w-full rounded-xl border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-700 px-3 py-2 text-sm" />
        </div>
        <button onClick={load} className="px-4 py-2 rounded-xl text-sm font-semibold text-white flex items-center gap-2" style={{ background: 'var(--theme-primary)' }}>
          <FontAwesomeIcon icon={faSearch} /> ค้นหา
        </button>
        <button onClick={load} className="px-3 py-2 rounded-xl text-sm bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-slate-300"><FontAwesomeIcon icon={faRotate} className={loading ? 'animate-spin' : ''} /></button>
      </div>

      <DataTable columns={columns} data={rows} loading={loading} keyExtractor={(t) => t.id} />

      {/* Attach slip modal */}
      <UiModal
        open={!!selected}
        onClose={closeAttach}
        title="แนบสลิปเติมเงิน Payout"
        icon={faUpload}
        iconColor="text-emerald-600"
        size="md"
        scrollable
        footer={
          <div className="flex flex-col sm:flex-row gap-2">
            <button onClick={() => complete(false)} disabled={!!processing || !slip} className="flex-1 px-4 py-2.5 rounded-xl text-sm font-semibold text-white" style={{ background: 'var(--theme-primary)' }}>
              {processing === 'verify' ? 'กำลังตรวจสอบ...' : 'ตรวจสอบ & เครดิต'}
            </button>
            {canForce && (
              <button onClick={() => complete(true)} disabled={!!processing || !slip} className="flex-1 px-4 py-2.5 rounded-xl text-sm font-semibold text-white bg-orange-500 hover:bg-orange-600 flex items-center justify-center gap-1.5">
                <FontAwesomeIcon icon={faExclamationTriangle} /> {processing === 'force' ? 'กำลังบังคับ...' : 'บังคับแนบ & เครดิต'}
              </button>
            )}
          </div>
        }
      >
        {selected && (
          <>
            <div className="bg-gray-50 dark:bg-slate-700/50 rounded-xl p-3 text-sm space-y-1">
              <div className="flex justify-between"><span className="text-gray-500 dark:text-slate-400">Merchant</span><span className="font-medium">{selected.merchant?.name}</span></div>
              <div className="flex justify-between"><span className="text-gray-500 dark:text-slate-400">ยอดฝาก</span><span className="font-bold" style={{ color: 'var(--theme-accent)' }}>฿{fmtMoney(selected.amount)}</span></div>
              <div className="flex justify-between"><span className="text-gray-500 dark:text-slate-400">เข้า Payout (net)</span><span className="font-bold text-emerald-600">฿{fmtMoney(selected.net_amount || 0)}</span></div>
              <div className="flex justify-between"><span className="text-gray-500 dark:text-slate-400">ผู้โอน</span><span>{selected.bank_name} {selected.bank_number}</span></div>
              {selected.receiving_bank && <div className="flex justify-between"><span className="text-gray-500 dark:text-slate-400">บัญชีรับ</span><span>{selected.receiving_bank.name} • {selected.receiving_bank.account_number}</span></div>}
            </div>

            <div className="mt-3">
              {slip ? (
                <div className="relative rounded-xl overflow-hidden border border-gray-200 dark:border-slate-600">
                  <img src={slip} alt="slip" className="w-full max-h-60 object-contain bg-gray-50 dark:bg-slate-800" />
                  <button type="button" onClick={() => setSlip('')} className="absolute top-2 right-2 bg-black/50 text-white rounded-full w-7 h-7 flex items-center justify-center"><FontAwesomeIcon icon={faXmark} /></button>
                </div>
              ) : (
                <label className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-gray-300 dark:border-slate-600 rounded-xl p-6 cursor-pointer hover:border-indigo-400 transition-colors">
                  <FontAwesomeIcon icon={faUpload} className="text-2xl text-gray-400" />
                  <span className="text-sm text-gray-500 dark:text-slate-400">แตะเพื่อเลือกรูปสลิป</span>
                  <input type="file" accept="image/*" className="hidden" onChange={onSlipChange} />
                </label>
              )}
            </div>

            <p className="text-[11px] text-gray-400 dark:text-slate-500 mt-2">"บังคับแนบ" จะข้ามการตรวจสอบสลิป แล้วเครดิตตามยอดที่ลูกค้ากรอก (ใช้กรณีสลิปตรวจไม่ผ่านแต่ยืนยันว่าเงินเข้าจริง)</p>

            {err && <div className="mt-2 bg-red-50 dark:bg-red-900/20 rounded-xl p-3 text-sm text-red-600 dark:text-red-400 flex items-start gap-2"><FontAwesomeIcon icon={faXmark} className="mt-0.5" />{err}</div>}
          </>
        )}
      </UiModal>

      {/* View slip */}
      <UiModal open={!!viewSlip} onClose={() => setViewSlip('')} title="สลิป" icon={faPaperclip} size="md" scrollable>
        {viewSlip && <img src={viewSlip} alt="slip" className="w-full rounded-xl" />}
      </UiModal>
    </div>
  );
};

export default PayoutTopupsPage;
